import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class DateHelperService {

  /**
   * Determines whether the provided date string is valid.
   *
   * @param dateString - The date string to validate.
   * @returns True if the date string is valid; otherwise, false.
   */
  public isValidDate(dateString: string): boolean {
     // Check for falsy value (covers empty string, null, or undefined)
      if (!dateString) {
        return false;
      }

      const date = this.toUtcDate(dateString);
      return !isNaN(date.getTime());
  }

  /**
   * Formats the given Date object into a string in 'YYYY-MM-DD' format.
   *
   * @param date - The Date object to format.
   * @returns A string representing the date in 'YYYY-MM-DD' format.
   */
  public formatDateForQuery(date: Date | string): string {
    const utcDate = this.toUtcDate(date);
    const year = utcDate.getUTCFullYear();
    // Months are zero-indexed so add 1, then pad to 2 digits.
    const month = ('0' + (utcDate.getUTCMonth() + 1)).slice(-2);
    // Pad day to 2 digits. Use UTC to avoid shifting dates across timezones.
    const day = ('0' + utcDate.getUTCDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  /**
   * Accepts a date as a string and formats it into 'YYYY-MM-DD'.
   *
   * @param dateStr - The input date string.
   * @returns A formatted date string in 'YYYY-MM-DD' format.
   * @throws An Error if the provided dateStr is not a valid date.
   */
  public formatStringDateForQuery(dateStr: string): string {
    if (!this.isValidDate(dateStr)) {
      throw new Error(`Invalid date string: ${dateStr}`);
    }

    const date = this.toUtcDate(dateStr);
    const year = date.getUTCFullYear();
    // Months are zero-indexed; pad with a leading zero if needed.
    const month = ('0' + (date.getUTCMonth() + 1)).slice(-2);
    // Pad day with a leading zero if needed. Use UTC to keep parity with server dates.
    const day = ('0' + date.getUTCDate()).slice(-2);

    return `${year}-${month}-${day}`;
  }

  /**
   * Normalize incoming date values so that ambiguous formats (e.g. 'YYYY-MM-DDTHH:mm:ss')
   * are treated as UTC rather than local time.
   */
  private toUtcDate(date: Date | string): Date {
    if (date instanceof Date) {
      // Clone the date to avoid mutating the original instance.
      return new Date(date.getTime());
    }

    const trimmed = date.trim();
    const hasIsoSeparator = trimmed.includes('T');
    const hasExplicitOffset = /[zZ]|[+-]\d{2}:\d{2}$/.test(trimmed);
    const hasClockTime = /\d{2}:\d{2}:\d{2}/.test(trimmed);

    let normalized = trimmed;

    if (hasClockTime && !hasIsoSeparator) {
      // Handle formats like "yyyy-MM-dd HH:mm:ss" by converting to ISO.
      const [datePart, timePart] = trimmed.split(' ');
      normalized = `${datePart}T${timePart}`;
    }

    if ((hasIsoSeparator || hasClockTime) && !hasExplicitOffset) {
      normalized = `${normalized}Z`;
    }

    return new Date(normalized);
  }
}
